import { get, isEmpty, isEqual, isNil, pick, reduce } from 'lodash';
import dayjs from 'dayjs';
import { Deal, PriceOverride, TradeIn } from './legos/dealTypes';
import { IncentivesProgram, Car as CarType, Car } from './legos/carTypes';
import { SelectInventoryGroupIdsAndDays } from './legos/incentiveTypes';
import { PriceRules } from './legos/priceRulesTypes';
import {
  ApplicationStatusCodes,
  UpstartApplicationStatus,
} from './legos/financingTypes';
import { DealUpgrade, Dealer, OwnershipRule } from './legos/dealerTypes';
import {
  CLIENT_TYPE,
  Condition,
  CrmType,
  FinanceType,
  FuelEfficiencyUnit,
  PRICE_OVERRIDE_THRESHOLD,
  Origin,
} from './constants';
import { Pencil, PencilData } from './legos/pencilTypes';
import { Accessory, SelectedAccessory } from './legos/accessoryTypes';
import {
  BaseOffer,
  FullLeaseOffer,
  FullOffer,
  FullPurchaseOffer,
  Offer,
  Payments,
} from './legos/paymentTypes';
import { fmtMoney } from './formatter';
import { PenProtectionPlanSourceTypes } from './legos/constants/dealer';
import { BaseOfferData } from './legos/deskingTypes';

// @TODO add proper types

const CREDIT_APPLICATION_EXPIRATION_PERIOD = 30; // in days

// https://github.com/teamupstart/upstart_web/blob/04219356e17c3bc4f23f13a850c9af99b88291a0/engines/common/context_groups/application/application_identity/lib/common/addresses/model.rb#L9
const UPSTART_ADDRESS_REGEX = /^(PSC (?=\d+))?((\S*\d\S*)|\w) .*$/;

// checks if a given date is more than 30 days ago
const overExpirationDays = (date: string | Date) => {
  const thirtyDaysAgo = dayjs().subtract(
    CREDIT_APPLICATION_EXPIRATION_PERIOD,
    'days',
  );
  return dayjs(date).isBefore(thirtyDaysAgo);
};

export const isDriverLicenseStepComplete = (deal: Deal): boolean =>
  !!deal.attachedFiles?.driverLicense;

export const isDocumentScanStepComplete = (deal: Deal): boolean =>
  deal.attachedFiles?.documents
    ? deal.attachedFiles.documents.length > 0
    : false;

export const isTradeInStepComplete = (deal: Deal) =>
  deal.tradeIns ? deal.tradeIns.length > 0 : false;

export const isCrmPushStepComplete = (deal: Deal) => !!deal.crmVisitID;

export const isCreditApplicationStepComplete = (deal: Deal) => {
  const { creditSubmittedAt } = deal;
  return !!creditSubmittedAt && !overExpirationDays(creditSubmittedAt);
};

export const isVehicleSelectionStepComplete = (deal: Deal) =>
  !isNil(deal.car_id);

export const isTestDriveStepComplete = (deal: Deal) => {
  const hasVehicle = isVehicleSelectionStepComplete(deal);
  return (
    deal?.testDriveAppointmentComplete ??
    (!isNil(deal.testDriveStartedAt) && hasVehicle)
  );
};

/**
 * Checks whether the provided priceOverride (and its respective source) matches with the given car and dealer
 */
const doesPriceOverrideMatch = ({
  car,
  dealer,
  source,
  priceOverride,
}: {
  car: Car;
  dealer: Dealer;
  source: Origin;
  priceOverride: PriceOverride;
}) => {
  // check if the price override is for the provided car
  if (!priceOverride || priceOverride.carId !== car.id) {
    return false;
  }
  // make sure price overrides for this source are enabled before using it
  if (!get(dealer, `featureConfig.globalPricing.usePriceOverrides.${source}`)) {
    return false;
  }
  // check against the expiration time for the price, if there is one
  if (priceOverride.expiry && priceOverride.expiry < dayjs().unix()) {
    return false;
  }
  // all checks were passed
  return true;
};

/**
 * Gets the price override for the given car from the given deal and dealer, returning null if there is none
 */
export const getCarPriceOverride = ({
  car,
  deal,
  dealer,
}: {
  car?: Car;
  deal?: Deal;
  dealer?: Dealer;
}): PriceOverride | null => {
  if (!car || !car.id || !deal || !deal.priceOverrides) {
    return null;
  }

  // find the price override for this car, if one exists
  // @TODO remove any when there are types
  const entry: any = Object.entries(deal.priceOverrides).find(
    ([source, priceOverride]: any) =>
      dealer && doesPriceOverrideMatch({ car, dealer, source, priceOverride }),
  );
  if (!entry) {
    return null;
  }
  const [, priceOverride] = entry;

  // gather all price values defined in this override
  const pricesToVerify: number[] = [priceOverride.carPrice];
  if (priceOverride.cachedPrice) {
    // @TODO remove any when there are types
    Object.values(priceOverride.cachedPrice).forEach((value: any) => {
      if (value && value.price) {
        pricesToVerify.push(value.price);
      }
    });
  }

  // compare all the price values as a fail-safe against large price changes
  // eslint-disable-next-line no-restricted-syntax
  for (const price of pricesToVerify) {
    // check if the price is valid
    if (price <= 0) {
      return null;
    }
    const priceDifference = Math.abs(price - (car.price || 0));
    const maxPriceDifference = (car.price || 0) * PRICE_OVERRIDE_THRESHOLD;
    // check if the price difference is lower than the max
    if (priceDifference > maxPriceDifference) {
      return null;
    }
  }

  return priceOverride;
};

/**
 * Get the matching offer from the cached price for the given car, taking into account overrides from the deal and dealer
 */
export const getCarCachedPriceOffer = ({
  car,
  deal,
  dealer,
  clientType: originalClientType,
  financeType: originalFinanceType,
}: {
  car?: Car;
  deal?: Deal;
  dealer?: Dealer;
  clientType?: CLIENT_TYPE;
  financeType?: FinanceType;
}) => {
  // by default use IN_STORE client type and CASH finance type
  const clientType = originalClientType || CLIENT_TYPE.IN_STORE;
  const financeType =
    originalFinanceType || (deal || {}).financeType || FinanceType.CASH;

  // get a matching price override for the car
  const priceOverride = getCarPriceOverride({ car, deal, dealer });

  // get the cached price
  const cachedPrice =
    get(priceOverride, ['cachedPrice', financeType]) ||
    get(car, ['cachedPrices', clientType, financeType]);

  // try to get the cached prop
  return cachedPrice;
};

const hasDisplayPrice = (value: SelectedOffer): value is FullOffer => {
  return !!value && 'displayPrice' in value;
};

const hasProtectionPlanInOffer = (
  value: SelectedOffer,
): value is FullPurchaseOffer | FullLeaseOffer => {
  return !!value && 'protectionPlans' in value;
};

/**
 * Get the price for the given car with the given client and finance type, taking into account overrides from the deal and dealer
 */
export const getCarPrice = ({
  car,
  deal,
  dealer,
  clientType,
  financeType,
  rawPrice,
  adjustDisplayPriceWithIncentives,
  adjustDisplayPriceWithAEP,
  dealerPriceRules,
}: {
  car?: Car;
  deal?: Deal;
  dealer?: Dealer;
  clientType?: CLIENT_TYPE;
  financeType?: FinanceType;
  rawPrice?: boolean;
  adjustDisplayPriceWithIncentives?: boolean;
  adjustDisplayPriceWithAEP?: boolean;
  dealerPriceRules?: PriceRules;
}): number | undefined => {
  // try to get the cached price for the car
  const cachedPriceOffer = getCarCachedPriceOffer({
    car,
    deal,
    dealer,
    clientType,
    financeType,
  });
  let programs: IncentivesProgram[];
  let displayPrice = 0;
  const includeAEPinPrice =
    car?.aep && car.aep > 0 && adjustDisplayPriceWithAEP;

  if (deal?.selectedOffer && hasDisplayPrice(deal?.selectedOffer)) {
    displayPrice = deal?.selectedOffer?.displayPrice;
  }

  const priceToDisplay = displayPrice ?? deal?.selectedOffer?.price;
  if (car?.id === deal?.car_id && !!priceToDisplay) {
    displayPrice = priceToDisplay;
  } else {
    displayPrice = cachedPriceOffer?.displayPrice || cachedPriceOffer?.price;
  }

  if (car?.id === deal?.car_id && deal?.selectedOffer?.programs) {
    programs = deal.selectedOffer.programs;
  } else {
    programs = cachedPriceOffer?.programs;
  }

  // If this Car has an Added Equipment Price
  // And if we have explicitly requested to include AEP in price
  if (includeAEPinPrice) {
    displayPrice += car.aep || 0;
    cachedPriceOffer.displayPrice += car.aep;
    cachedPriceOffer.price += car.aep;
  }
  // If Dealer is Configured to include incentives in displayPrice,
  // Let's do that calculation now, shall we?
  if (
    adjustDisplayPriceWithIncentives &&
    programs?.length > 0 &&
    displayPrice &&
    !rawPrice
  ) {
    let totalAppliedCash = 0;
    programs.forEach((p: IncentivesProgram) => {
      if (clientType) {
        const enableSelectInventoryRebate = !!dealerPriceRules?.data?.[
          clientType
        ]?.New?.enableSelectInventoryRebate;
        const isSelectInventoryIncentive = Object.keys(
          SelectInventoryGroupIdsAndDays,
        ).includes(`${p.groupId}`);
        if (isSelectInventoryIncentive && !enableSelectInventoryRebate) {
          // Is select inventory rebate, but the config to include in the price as default is off, so we bail out here
          return;
        }
      }
      if (p.appliedCash > 0) {
        totalAppliedCash += p.appliedCash;
      }
    });
    return displayPrice - totalAppliedCash;
  }

  // Cached Price is for when car does not match car id in deal
  if (cachedPriceOffer?.displayPrice && !rawPrice) {
    return cachedPriceOffer.displayPrice;
  }
  if (cachedPriceOffer?.price) {
    return cachedPriceOffer.price;
  }

  // return car.price with aep if requested
  if (includeAEPinPrice && car.price && car.aep) {
    const priceWithAEP = car.price + car.aep;
    return priceWithAEP;
  }

  // default to car.price
  return car?.price ?? undefined;
};

/**
 * Determines if a financeType is available for a Car - used mostly in backend for filtering list of cars, given a financeType
 *
 * @param car
 * @param carPrice
 * @param dealer
 * @param clientType
 * @param financeType
 */
export const isFinanceTypeAllowedForCar = ({
  car,
  carPrice,
  dealer,
  clientType,
  financeType,
}: {
  car: Car;
  carPrice: number;
  dealer: Dealer;
  clientType: CLIENT_TYPE;
  financeType?: FinanceType;
}): boolean => {
  // If no car condition available then how to even find the correct ownership rule ?
  if (!car.condition || financeType === undefined) {
    return false;
  }

  // No Lease for used cars
  if (financeType === FinanceType.LEASE && car?.condition !== Condition.NEW) {
    return false;
  }

  // Backwards compatibility + Allows all if nothing is configured yet
  if (!dealer.ownershipOptionsRules || isEmpty(dealer.ownershipOptionsRules)) {
    return true;
  }

  const ownershipRule =
    dealer.ownershipOptionsRules?.[car.condition]?.[clientType]?.[financeType];
  if (!ownershipRule || !ownershipRule.enabled) {
    return false;
  }

  const carHasHigherMileageThanAllowed =
    car.mileage && ownershipRule.maxMiles
      ? car.mileage > ownershipRule.maxMiles
      : false;
  const carIsOlderThanAllowed =
    car.year && ownershipRule.maxAge
      ? new Date().getFullYear() - car.year > ownershipRule.maxAge
      : false;
  const carIsMoreExpensiveThanAllowed = ownershipRule.maxPrice
    ? carPrice > ownershipRule.maxPrice
    : false;
  if (
    carHasHigherMileageThanAllowed ||
    carIsOlderThanAllowed ||
    carIsMoreExpensiveThanAllowed
  ) {
    return false;
  }
  return true;
};

/**
 * Determines if a financeType is available for a Car that is already in a Deal - used mostly in clients to determine if tabs for financeTypes appear or not
 */
export const isFinanceTypeAllowedForCarWithDeal = ({
  car,
  deal,
  dealer,
  clientType,
  financeType,
}: {
  car: Car;
  deal: Deal;
  dealer: Dealer;
  clientType: CLIENT_TYPE;
  financeType: FinanceType;
}): boolean => {
  const carPrice = getCarPrice({ car, deal, dealer, clientType, financeType });
  return isFinanceTypeAllowedForCar({
    car,
    carPrice: carPrice || 0,
    dealer,
    clientType,
    financeType,
  });
};

/**
 * Return all financeTypes available for a car
 */
export const getAllFinanceTypesForCar = ({
  car,
  deal,
  dealer,
  clientType,
}: {
  car: Car;
  deal: Deal;
  dealer: Dealer;
  clientType: CLIENT_TYPE;
}): FinanceType[] =>
  Object.values(FinanceType).filter(financeType =>
    isFinanceTypeAllowedForCarWithDeal({
      car,
      deal,
      dealer,
      clientType,
      financeType,
    }),
  );

/**
 * Return all financeTypes available for a dealer
 */
export const getAllFinanceTypesForDealer = ({
  dealer,
  clientType,
}: {
  dealer: Dealer;
  clientType: CLIENT_TYPE;
}): FinanceType[] => {
  const allowedFinanceTypes: {
    [index in FinanceType]?: boolean | undefined;
  } = {};
  Object.values(Condition).forEach(condition => {
    if (dealer.ownershipOptionsRules?.[condition]?.[clientType]) {
      Object.keys(dealer.ownershipOptionsRules[condition][clientType]).forEach(
        (financeType: string) => {
          const financeTypeEnum: FinanceType = <FinanceType>financeType;
          const ownershipRule: OwnershipRule | undefined | null =
            dealer.ownershipOptionsRules?.[condition]?.[clientType]?.[
              financeTypeEnum
            ];
          if (ownershipRule && ownershipRule.enabled) {
            allowedFinanceTypes[financeType as FinanceType] = true;
          }
        },
      );
    }
  });
  // Convert the keys into a plain array then do the mapping to satisfy TS typing.
  return Object.keys(allowedFinanceTypes).map(
    financeType => financeType as FinanceType,
  );
};

/**
 * Gets the city and highway fuel efficiency for the given car in the given unit
 */
export const getFuelEfficiency = ({
  car,
  dealer,
}: {
  car?: CarType;
  dealer?: {
    localizationConfig?: {
      fuelEfficiencyUnit?: FuelEfficiencyUnit;
    } | null;
  };
}) => {
  const unit =
    dealer?.localizationConfig?.fuelEfficiencyUnit || FuelEfficiencyUnit.MPG;

  const result: {
    city?: number | null;
    highway?: number | null;
    rawFeedValue?: string;
    unit: FuelEfficiencyUnit;
  } = {
    ...car?.fuelEfficiency?.[unit as FuelEfficiencyUnit],
    unit,
  };

  const { rawFeedValue } = car?.fuelEfficiency ?? {};
  if (rawFeedValue) {
    result.rawFeedValue = rawFeedValue;
  }

  return result;
};

export const upstartApplicationStatusIsDenied = (
  status: UpstartApplicationStatus,
) => {
  return (
    status.code === ApplicationStatusCodes.Disqualified ||
    status.code === ApplicationStatusCodes.Cancelled
  );
};

/**
 * Returns if deal has PII or not. Currently, implemented mainly for Online deals (which require firstName, lastName, email, and phone)
 * @param {Deal} deal - Deal Object
 * @returns {boolean} - If deal has PII or not.
 */
export const hasPII = (deal?: Deal | null): boolean => {
  if (!deal) {
    return false;
  }
  const { applicant } = deal;
  return applicant
    ? !!applicant.firstName &&
        !!applicant.lastName &&
        !!applicant.email &&
        !!applicant.phone
    : false;
};

/**
 * Finds the index of a given trade-in in a list of trade-ins.
 *
 * @param {TradeIn} tradeIn - The trade-in object to search for in the list.
 * @param {TradeIn[]} tradeInList - The list of trade-ins to search through.
 * @returns {number} The index of the trade-in in the list, or -1 if it was not found.
 */
export function findTradeInIndex(
  tradeIn: TradeIn,
  tradeInList: TradeIn[],
): number {
  // return early if the list is empty
  if (!tradeInList?.length) return -1;

  if (tradeIn && tradeIn.id) {
    // if trade has id - try to find the exact match using id + tradeInAllowance + tradeACV + payOff
    for (let i = 0; i < tradeInList.length; i += 1) {
      const t = tradeInList[i];
      if (
        t &&
        t.id === tradeIn.id &&
        t.tradeInAllowance === tradeIn.tradeInAllowance &&
        t.tradeACV === tradeIn.tradeACV &&
        t.payOff === tradeIn.payOff
      ) {
        return i;
      }
    }

    // if trade has id and there is no exact match above - try to find the match using id only
    for (let i = 0; i < tradeInList.length; i += 1) {
      const t = tradeInList[i];
      if (t && t.id === tradeIn.id) {
        return i;
      }
    }
  }

  if (
    tradeIn &&
    !tradeIn.id &&
    !isNil(tradeIn.make) &&
    !isNil(tradeIn.model) &&
    !isNil(tradeIn.year)
  ) {
    for (let i = 0; i < tradeInList.length; i += 1) {
      const t = tradeInList[i];
      // If the make, model, and year match we can reasonably assume that this tradeIn is a match
      if (
        t &&
        isEqual(t.make, tradeIn.make) &&
        isEqual(t.year, tradeIn.year) &&
        isEqual(t.model, tradeIn.model)
      ) {
        return i;
      }
    }
  }

  // return -1 if none of the conditions above worked
  return -1;
}

const COMMON_OFFER_PROPS = ['deskingOfferId', 'months', 'mileage', 'category'];

/**
 * Checks if deal has tradeIn(s) or not.
 *
 * @param {Pick<Deal, 'tradeIns'>} tradeIns - tradeIns
 * @returns {boolean} If tradeIns in deal or not.
 */
export const hasTradeIn = (tradeIns: Pick<Deal, 'tradeIns'>): boolean =>
  tradeIns.tradeIns?.length ? tradeIns.tradeIns.length > 0 : false;

/**
 * Checks if deal contains vehicle payment info.
 *
 * @param {Deal} deal - Deal Object
 * @returns {boolean} If deal has payment info.
 */
export const hasPayment = (deal: Deal): boolean => {
  if (deal.selectedOffer) {
    if ('estimatedMonthlyPayment' in deal.selectedOffer) {
      return !!deal.selectedOffer.estimatedMonthlyPayment;
    }

    if ('totalDue' in deal.selectedOffer) {
      return !!deal.selectedOffer.totalDue;
    }
  }

  return false;
};

/**
 * Checks if deal has any protection plan selected or not.
 *
 * @param {Deal} deal
 * @param {Dealer} dealer
 * @returns {boolean} Deal has protection plan or not.
 */
export const hasProtectionPlan = (deal: Deal, dealer: Dealer): boolean => {
  const ProtectionPlanSource = dealer.protectionPlanSource;
  if (
    ProtectionPlanSource &&
    ProtectionPlanSource === PenProtectionPlanSourceTypes.PEN &&
    deal.selectedProducts
  ) {
    return deal.selectedProducts?.length > 0;
  }
  return !!deal.upgrades?.selectedUpgrades;
};

/**
 * Deal/Pencil contains accessories or not.
 *
 * @param {Deal} deal
 * @param {Pencil | undefined} pencil
 * @returns {boolean} True if accessories available. Else, false.
 */
export const hasAccessories = (deal: Deal, pencil?: Pencil): boolean => {
  const accessories =
    pencil?.data?.selectedAccessories ?? deal?.selectedAccessories ?? [];
  return accessories?.length > 0;
};

/**
 * Deal/Pencil contains accessories or not.
 * Web prioritizes deal over pencil, so this logic is in its own function.
 *
 * @param {Deal} deal
 * @param {Pencil | undefined} pencil
 * @returns {boolean} True if accessories available. Else, false.
 */
export const hasOnlineDealAccessories = (
  deal: Deal,
  pencil?: Pencil,
): boolean => {
  const accessories =
    deal?.selectedAccessories ?? pencil?.data?.selectedAccessories ?? [];
  return accessories?.length > 0;
};
/**
 * Checks if applicant has signed the deal or not.
 *
 * @param {Deal} deal
 * @returns {boolean} Deal contains signed summary/ownership document.
 */
export const hasOwnershipOptions = (deal: Deal): boolean => {
  return !!deal?.attachedFiles?.dealSummarySignature;
};

/**
 * Checks if pricingReview is attached to deal or not.
 *
 * @param {Deal} deal
 * @returns {boolean} Deal contains pricing review document attached.
 */
export const hasPricingReview = (deal: Deal): boolean => {
  return !!deal?.attachedFiles?.pricingReview;
};

/**
 * Checks if two offers are the same (selected).
 *
 * @param {SelectedOffer} selectedOffer
 * @param {DataFromOfferOrPencil} offer
 * @returns {boolean}
 */
export const isSelected = ({
  selectedOffer,
  offer,
}: {
  selectedOffer: SelectedOffer;
  offer: DataFromOfferOrPencil;
}): boolean => {
  if (!selectedOffer || !offer) return false;
  return isEqual(
    pick(selectedOffer, COMMON_OFFER_PROPS),
    pick(offer, COMMON_OFFER_PROPS),
  );
};

/**
 * Fetches the selected offer from the pencil.
 *
 * @param {Pencil | undefined} pencil
 * @param {SelectedOffer} selectedOffer
 * @param {FinanceType} financeType
 * @returns {SelectedOffer} selected offer from the pencil.
 */
export const getSelectedOfferFromPencil = ({
  pencil,
  selectedOffer,
  financeType,
}: {
  pencil?: Pencil;
  selectedOffer: SelectedOffer;
  financeType: FinanceType;
}): SelectedOffer | BaseOfferData => {
  if (!selectedOffer || !pencil) {
    return undefined;
  }

  return pencil?.data?.[financeType]?.find((offer: BaseOffer | BaseOfferData) =>
    isSelected({ selectedOffer, offer }),
  );
};

/**
 * Get specific data from a payment offer.
 *
 * @param {Payments} payments
 * @param {SelectedOffer} selectedOffer
 * @param {FinanceType} financeType
 * @param {string} Field
 * @returns {any} Returns one of the properties of an offer that is requested.
 */
export const getDataFromPaymentOffer = ({
  payments,
  selectedOffer,
  financeType,
  field,
}: {
  payments?: Payments;
  selectedOffer: SelectedOffer;
  financeType?: FinanceType;
  field: string;
}): any => {
  const offersForFinanceType:
    | DataFromOfferOrPencil[]
    | undefined = payments?.allOffers?.find(
    offer => offer.financeType === financeType,
  )?.offers;
  const selectedPaymentOffer = offersForFinanceType?.find(
    (offer: DataFromOfferOrPencil) => isSelected({ selectedOffer, offer }),
  );
  return selectedPaymentOffer?.[field as keyof DataFromOfferOrPencil];
};

type DataFromOfferOrPencil = Offer | FullOffer | BaseOfferData | undefined;
type SelectedOffer = Offer | FullOffer | null | undefined;
type OfferOrPencil = BaseOffer | BaseOfferData | PencilData;

const isBaseOffer = (value: OfferOrPencil): value is BaseOffer => {
  return !('selectedAccessories' in value);
};

/**
 * Get Data from an offer or pencil
 *
 * @param {Pencil | undefined} pencil - pencil of the deal.
 * @param {SelectedOffer} selectedOffer
 * @param {FinanceType} financeType - If the offer is a purchase, lease or cash offer.
 * @returns {any} - Returns one of the properties of the selected offer that is requested.
 */
export const getDataFromOfferOrPencil = ({
  pencil,
  selectedOffer,
  financeType,
}: {
  pencil?: Pencil;
  selectedOffer: SelectedOffer;
  field?: string;
  /* TODO: Remove Field */ financeType: FinanceType;
}): SelectedAccessory[] | undefined => {
  const offerOrPencil: OfferOrPencil | null | undefined =
    getSelectedOfferFromPencil({ pencil, selectedOffer, financeType }) ??
    pencil?.data;

  if (!!offerOrPencil && isBaseOffer(offerOrPencil)) {
    return undefined;
  }

  return offerOrPencil?.selectedAccessories ?? undefined;
};

/**
 * Get required data from payment offer, selected offer, or pencil
 *
 * @param {Payments | undefined} payments
 * @param {Pencil | undefined} pencil
 * @param {SelectedOffer} selectedOffer
 * @param {FinanceType} financeType
 * @param {string} field
 * @returns {any}
 */
export const getDataFromPaymentOfferOrPencil = ({
  payments,
  pencil,
  selectedOffer,
  financeType,
  field,
}: {
  payments?: Payments;
  pencil?: Pencil;
  selectedOffer: SelectedOffer;
  financeType: FinanceType;
  field: string;
}): SelectedAccessory[] | undefined => {
  return (
    getDataFromPaymentOffer({ payments, selectedOffer, financeType, field }) ??
    getDataFromOfferOrPencil({ pencil, selectedOffer, financeType, field })
  );
};

/**
 * Fetch accessories from deal or pencil
 *
 * @param {Deal | undefined} deal
 * @param {Payments | unedfined} payments
 * @param {Pencil | undefined} pencil
 * @returns {(SelectedAccessory | Accessory)[]}
 */
export const extractAccessories = ({
  deal,
  payments,
  pencil,
}: {
  deal?: Deal;
  payments?: Payments;
  pencil?: Pencil;
}): (SelectedAccessory | Accessory)[] => {
  const pencilAccessories = getDataFromPaymentOfferOrPencil({
    payments,
    pencil,
    selectedOffer: deal?.selectedOffer,
    financeType: deal?.financeType ?? get(deal, 'selectedOffer.financeType'),
    field: 'selectedAccessories',
  });
  const dealAccessories = deal?.selectedAccessories;
  return pencilAccessories ?? dealAccessories ?? [];
};

/**
 * Get Total Cost of the upgrades/protection plans
 *
 * @param {DealUpgrade} upgrade
 * @returns {number}
 */
export const getTotalPlanCost = (upgrade: DealUpgrade): number => {
  return reduce(
    upgrade?.content,
    (sum, item: any) => {
      return sum + item?.cost ?? 0;
    },
    0,
  );
};

/**
 * Get Protection Plan Cost or Upgrades Cost as per financeType
 *
 * @param {Deal} deal
 * @param {DealUpgrade} plan
 * @returns {number}
 */
export const getValue = ({
  deal,
  plan,
}: {
  deal: Deal;
  plan: DealUpgrade;
}): number => {
  const planCost = getTotalPlanCost(plan);
  if (deal.financeType === FinanceType.CASH) {
    return planCost;
  }

  const selectedoffer = deal.selectedOffer;

  if (
    plan?.title &&
    selectedoffer &&
    hasProtectionPlanInOffer(selectedoffer) &&
    !!selectedoffer.protectionPlans
  ) {
    return selectedoffer?.protectionPlans?.[plan.title];
  }

  return 0;
};

/**
 * Get a string formatted with cost
 *
 * @param {Deal} deal
 * @param {DealUpgrade} plan
 * @returns {string}
 */
export const getCostString = ({
  deal,
  plan,
}: {
  deal: Deal;
  plan: DealUpgrade;
}): string => {
  const value: any = getValue({ deal, plan });
  return deal.financeType === FinanceType.CASH
    ? fmtMoney(value)
    : `${fmtMoney(value)}/month`;
};

/**
 * returns true if visitID is compatible with eLeads magic sauce
 *   visit id points to opportunity resource:- https://www.eleadcrm.com/evo2/fresh/elead-v45/elead_track/NewProspects/OpptyDetails.aspx?lPID=<PID>&lDID=<DID>
 * @param visitID
 * @returns boolean
 */
export const isVisitIDMagicCompatible = (
  visitID: Deal['crmVisitID'],
): boolean => {
  if (!visitID) {
    return false;
  }

  const urlPattern = new RegExp(
    '(https:\\/\\/www\\.eleadcrm\\.com\\/evo2\\/fresh\\/elead-v45\\/elead_track\\/NewProspects\\/OpptyDetails\\.aspx)',
    'gi',
  );
  const personIDPattern = new RegExp('lPID=(?<pid>\\S+)', 'gi');
  const dealIDPattern = new RegExp('(lDID=(?<did>\\S+))*', 'gi');

  // all patterns must match
  return (
    urlPattern.test(visitID) &&
    personIDPattern.test(visitID) &&
    dealIDPattern.test(visitID)
  );
};

/**
 * Checks visitID requirements based on crmType of dealer.
 *
 * @param dealer
 * @param deal
 * @param dealerFlags
 */
export const isVisitIDPresent = ({
  dealer,
  deal,
}: {
  dealer: Pick<Dealer, 'crmType'>;
  deal: Pick<Deal, 'crmVisitID' | 'crmMeta'>;
}): boolean => {
  switch (dealer.crmType) {
    case CrmType.ELEAD:
      return !!deal.crmMeta?.customerId;
    default:
      return !!deal.crmVisitID;
  }
};

/**
 * Checks if the address line 1 field is in valid format
 * @param address
 */
export const isValidUpstartAddress = (address?: string): boolean => {
  return !!(address && UPSTART_ADDRESS_REGEX.test(address));
};

export const isReadonlyInCrm = (deal: Pick<Deal, 'crmMeta'>): boolean => {
  return !!deal.crmMeta?.isSold || !!deal.crmMeta?.isInactive;
};
